import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarCheck, Clock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/book`;

const dayLabel = (stamp) => new Date(stamp).toLocaleDateString(undefined, {
  weekday: "long", day: "numeric", month: "long",
});
const timeLabel = (stamp) => new Date(stamp).toLocaleTimeString(undefined, {
  hour: "2-digit", minute: "2-digit",
});

export default function BookingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/${slug}`)
      .then(({ data: payload }) => setData(payload))
      .catch((e) => setError(e.response?.data?.detail || "That booking link is not live"));
  }, [slug]);

  const byDay = useMemo(() => {
    const groups = {};
    (data?.slots || []).forEach((stamp) => {
      const key = stamp.slice(0, 10);
      groups[key] = groups[key] || [];
      groups[key].push(stamp);
    });
    return Object.entries(groups).slice(0, 10);
  }, [data]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data: payload } = await axios.post(`${API}/${slug}`, { ...form, starts_at: slot });
      setDone(payload);
    } catch (e) {
      setError(e.response?.data?.detail || "That did not go through — please try again");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded-lg border border-[#1b2a22]/15 bg-white px-4 py-3 text-sm text-[#0E1A13] outline-none transition-colors duration-200 placeholder:text-[#0E1A13]/30 focus:border-[#C8A24A]";

  return (
    <div className="min-h-screen bg-[#F7F4EC] px-6 py-16" data-testid="booking-page">
      <div className="mx-auto max-w-2xl">
        <p className="font-serif text-2xl text-[#0E1A13]">OneHarvestCo. Benefits</p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#9A6A3A]">
          Group benefits · Houston, Texas
        </p>

        {done ? (
          <div className="mt-12 rounded-2xl border border-[#0E1A13]/10 bg-white p-8"
               data-testid="booking-confirmed">
            <CalendarCheck className="h-6 w-6 text-[#C8A24A]" />
            <h1 className="mt-4 font-serif text-3xl font-light text-[#0E1A13]">
              You're booked in
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-[#0E1A13]/70">
              {done.type_name} on {dayLabel(done.starts_at)} at {timeLabel(done.starts_at)}.
              A confirmation is on its way to {form.email}. Reply to it if you need a different time.
            </p>
          </div>
        ) : error && !data ? (
          <p className="mt-12 text-sm text-[#9A3A2A]" data-testid="booking-error">{error}</p>
        ) : !data ? (
          <p className="mt-12 flex items-center gap-2 text-sm text-[#0E1A13]/50">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the diary…
          </p>
        ) : (
          <>
            <h1 className="mt-10 font-serif text-4xl font-light leading-tight text-[#0E1A13] sm:text-5xl"
                data-testid="booking-title">
              {data.type.name}
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-[#0E1A13]/70">
              {data.type.blurb}
            </p>
            <p className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#0E1A13]/40">
              <Clock className="h-3 w-3" /> {data.type.minutes} minutes · times shown in your own
              timezone
            </p>

            <div className="mt-10 space-y-6">
              {byDay.map(([day, slots]) => (
                <div key={day} data-testid={`booking-day-${day}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0E1A13]/40">
                    {dayLabel(slots[0])}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {slots.map((stamp) => (
                      <button key={stamp} type="button" data-testid={`booking-slot-${stamp}`}
                              onClick={() => setSlot(stamp)}
                              className={`rounded-full border px-4 py-2 text-sm transition-all duration-200 ${
                                slot === stamp
                                  ? "border-[#0E1A13] bg-[#0E1A13] text-[#F7F4EC]"
                                  : "border-[#0E1A13]/15 bg-white text-[#0E1A13]/75 hover:border-[#C8A24A] hover:-translate-y-0.5"}`}>
                        {timeLabel(stamp)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {byDay.length === 0 ? (
                <p className="text-sm text-[#0E1A13]/50" data-testid="booking-no-slots">
                  No open times in the next few weeks — email kyle@ohcbenefits.com and I'll find one.
                </p>
              ) : null}
            </div>

            {slot ? (
              <form onSubmit={submit} className="mt-10 space-y-4 rounded-2xl border border-[#0E1A13]/10 bg-white p-6"
                    data-testid="booking-form">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A6A3A]">
                  {dayLabel(slot)} at {timeLabel(slot)}
                </p>
                <input required placeholder="Your name" className={input} value={form.name}
                       data-testid="booking-name-input"
                       onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required type="email" placeholder="Work email" className={input}
                       value={form.email} data-testid="booking-email-input"
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input placeholder="Company" className={input} value={form.company}
                       data-testid="booking-company-input"
                       onChange={(e) => setForm({ ...form, company: e.target.value })} />
                <input placeholder="Phone (optional)" className={input} value={form.phone}
                       data-testid="booking-phone-input"
                       onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <textarea rows={3} placeholder="Anything I should know before the call?"
                          className={input} value={form.notes} data-testid="booking-notes-input"
                          onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                {error ? (
                  <p className="text-sm text-[#9A3A2A]" data-testid="booking-form-error">{error}</p>
                ) : null}
                <button type="submit" disabled={busy} data-testid="booking-submit"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0E1A13] px-6 py-3 text-sm font-medium text-[#F7F4EC] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#9A6A3A] disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {busy ? "Booking…" : "Confirm the time"}
                </button>
              </form>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
